/**
 * Parses the human-readable `draw_schedule` strings on games
 * ("Mon, Wed, Sat 10:59 PM ET", "Daily 6:59 PM ET", "Mon-Fri 6:55 PM ET")
 * into draw days + the UTC instant a given day's draw happens.
 */

const DAY_INDEX: Record<string, number> = {
  sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
};

const ZONE_STANDARD_OFFSET: Record<string, number> = { ET: -5, CT: -6, MT: -7, PT: -8, AT: -4, HT: -10 };

export interface ParsedSchedule {
  /** Days of week (0=Sun) the game is drawn. Empty set = unknown -> treat as daily. */
  days: Set<number>;
  /** Minutes after midnight in the game's local zone, or null when unknown. */
  minutes: number | null;
  zone: string;
}

function nthSundayUtc(year: number, month: number, nth: number): number {
  // month 0-based; returns UTC ms of the nth Sunday at 2am local-ish (approx, UTC midday guard)
  const first = Date.UTC(year, month, 1);
  const dow = new Date(first).getUTCDay();
  const day = 1 + ((7 - dow) % 7) + (nth - 1) * 7;
  return Date.UTC(year, month, day, 7, 0, 0);
}

/** Approximate US DST: second Sunday in March -> first Sunday in November. */
export function isUsDst(dateIsoUtcMs: number): boolean {
  const y = new Date(dateIsoUtcMs).getUTCFullYear();
  return dateIsoUtcMs >= nthSundayUtc(y, 2, 2) && dateIsoUtcMs < nthSundayUtc(y, 10, 1);
}

export function parseDrawSchedule(schedule?: string | null): ParsedSchedule {
  const s = (schedule || "").trim();
  const zoneMatch = s.match(/\b(ET|CT|MT|PT|AT|HT)\b/i);
  const zone = zoneMatch ? zoneMatch[1].toUpperCase() : "ET";

  const timeMatch = s.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  let minutes: number | null = null;
  if (timeMatch) {
    let h = parseInt(timeMatch[1], 10) % 12;
    if (timeMatch[3].toUpperCase() === "PM") h += 12;
    minutes = h * 60 + parseInt(timeMatch[2], 10);
  }

  const days = new Set<number>();
  const lower = s.toLowerCase();
  if (/daily|every day|same as host/.test(lower) || !s) {
    for (let i = 0; i < 7; i++) days.add(i);
  } else {
    // ranges like mon-fri / mon-sat
    const rangeRe = /(sun|mon|tue|wed|thu|fri|sat)\s*-\s*(sun|mon|tue|wed|thu|fri|sat)/g;
    let m: RegExpExecArray | null;
    let consumed = lower;
    while ((m = rangeRe.exec(lower))) {
      const a = DAY_INDEX[m[1]];
      const b = DAY_INDEX[m[2]];
      for (let i = a; ; i = (i + 1) % 7) {
        days.add(i);
        if (i === b) break;
      }
      consumed = consumed.replace(m[0], " ");
    }
    const single = consumed.match(/\b(sun|mon|tue|wed|thu|fri|sat)\b/g) || [];
    for (const d of single) days.add(DAY_INDEX[d]);
    if (days.size === 0) for (let i = 0; i < 7; i++) days.add(i);
  }

  return { days, minutes, zone };
}

/** Does this game draw on the given ISO (YYYY-MM-DD) date? */
export function drawsOnDate(schedule: string | null | undefined, isoDate: string): boolean {
  const { days } = parseDrawSchedule(schedule);
  const dow = new Date(`${isoDate}T00:00:00Z`).getUTCDay();
  return days.has(dow);
}

/** UTC ms when the draw for `isoDate` takes place (defaults to 11:59 PM local when unknown). */
export function drawInstantUtc(schedule: string | null | undefined, isoDate: string): number {
  const { minutes, zone } = parseDrawSchedule(schedule);
  const midnightUtc = Date.parse(`${isoDate}T00:00:00Z`);
  const std = ZONE_STANDARD_OFFSET[zone] ?? -5;
  const offset = isUsDst(midnightUtc) && zone !== "HT" ? std + 1 : std;
  const mins = minutes ?? 23 * 60 + 59;
  return midnightUtc + (mins - offset * 60) * 60000;
}

/** Has the draw for this date already happened (with a small results-posting buffer)? */
export function hasDrawOccurred(
  schedule: string | null | undefined,
  isoDate: string,
  now: Date = new Date(),
  bufferMinutes = 15,
): boolean {
  return now.getTime() >= drawInstantUtc(schedule, isoDate) + bufferMinutes * 60000;
}
